import { useState } from 'react';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', project_type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const fullMessage = `[Quote Request]\n${form.company ? `Company: ${form.company}\n` : ''}${form.project_type ? `Project Type: ${form.project_type}\n` : ''}\n${form.message}`;
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone || '', message: fullMessage }),
      });
      if (!res.ok) throw new Error('Failed');
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', company: '', project_type: '', message: '' });
    } catch {
      setError('Failed to send. Please email inquiry@plangenerator.com.au directly.');
    }
    setSubmitting(false);
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <PublicLayout>
      <SEO
        title="Contact — Request a Waste Management Plan Quote"
        description="Request a quote for your waste management plan. Send us your plans and we'll provide a tailored proposal within 24 hours. Servicing NSW and VIC developments."
        keywords="waste management plan quote, WMP quote Sydney, WMP quote Melbourne, waste management consultant contact, request a waste management plan"
        canonicalPath="/contact"
      />

      <section className="py-16 lg:py-20 bg-[#F8FAFC]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
            {/* Left — Info */}
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">CONTACT US</div>
              <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-[#0A2540] leading-tight mb-4" data-testid="contact-headline">
                Request a Quote
              </h1>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Send us your plans and project details &mdash; we&apos;ll provide a tailored proposal, usually within 24 hours. No obligation, no lock-in.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0A2540]">Phone</p>
                    <a href="tel:0431469974" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">0431 469 974</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0A2540]">Email</p>
                    <a href="mailto:inquiry@plangenerator.com.au" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">inquiry@plangenerator.com.au</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0A2540]">Service Areas</p>
                    <p className="text-sm text-slate-600">Sydney &amp; Melbourne</p>
                    <p className="text-xs text-slate-500 mt-0.5">Servicing all NSW &amp; VIC councils</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0A2540]">ABN</p>
                    <p className="text-sm text-slate-600">922 849 40596</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-5 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-sm font-semibold text-[#0A2540] mb-1">What to include in your enquiry</p>
                <ul className="text-xs text-slate-600 space-y-1 leading-relaxed">
                  <li>&#x2022; Project address and council</li>
                  <li>&#x2022; Type of development (residential, commercial, mixed-use)</li>
                  <li>&#x2022; Number of dwellings/units or GFA</li>
                  <li>&#x2022; Application type (DA, CC, CDC, SSDA, Green Star)</li>
                  <li>&#x2022; Architectural plans (if available)</li>
                </ul>
              </div>
            </div>

            {/* Right — Form */}
            <div>
              {success ? (
                <div className="bg-white rounded-2xl border border-emerald-200 shadow-[0_4px_24px_rgb(0,0,0,0.04)] p-10 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-[#0A2540] mb-2">Quote Request Sent</h3>
                  <p className="text-sm text-slate-500">Thank you. We&apos;ll review your enquiry and get back to you within 1 business day.</p>
                  <button onClick={() => setSuccess(false)} className="mt-6 text-sm text-blue-600 hover:text-blue-700 font-medium">Send another enquiry</button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgb(0,0,0,0.04)] p-8" data-testid="contact-form-card">
                  <h2 className="text-lg font-bold text-[#0A2540] mb-1">Send us your project details</h2>
                  <p className="text-xs text-slate-500 mb-6">We&apos;ll respond with a tailored quote, usually within 24 hours.</p>
                  {error && <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-xl text-xs mb-4">{error}</div>}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Name *</label>
                        <input type="text" required value={form.name} onChange={update('name')}
                          className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                          placeholder="Your name" data-testid="contact-name" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Email *</label>
                        <input type="email" required value={form.email} onChange={update('email')}
                          className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                          placeholder="you@example.com" data-testid="contact-email" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                        <input type="tel" value={form.phone} onChange={update('phone')}
                          className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                          placeholder="04XX XXX XXX" data-testid="contact-phone" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Company</label>
                        <input type="text" value={form.company} onChange={update('company')}
                          className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                          placeholder="Your company" data-testid="contact-company" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Project / Application Type</label>
                      <select value={form.project_type} onChange={update('project_type')}
                        className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                        data-testid="contact-project-type">
                        <option value="">Select type (optional)</option>
                        <option value="Operational WMP (DA)">Operational WMP (DA)</option>
                        <option value="D&CWMP">Demolition &amp; Construction WMP</option>
                        <option value="Stormwater Management Plan">Stormwater Management Plan</option>
                        <option value="Construction Management Plan">Construction Management Plan</option>
                        <option value="Green Star Credit">Green Star Credit (7A / 8A / 22B)</option>
                        <option value="SSDA">State Significant Development (SSDA)</option>
                        <option value="HAZMAT Survey">HAZMAT Survey</option>
                        <option value="Waste Audit">Waste Audit</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Project Details *</label>
                      <textarea required value={form.message} onChange={update('message')} rows={5}
                        className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all resize-none"
                        placeholder="Tell us about your project — address, council, number of dwellings/units, and any specific requirements..."
                        data-testid="contact-message" />
                    </div>
                    <button type="submit" disabled={submitting}
                      className="w-full py-3 rounded-full bg-[#0A2540] text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50 shadow-lg shadow-slate-900/10"
                      data-testid="contact-submit">
                      {submitting ? 'Sending...' : 'Send Quote Request'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="py-12 bg-white border-t border-slate-100" data-testid="contact-areas">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-lg font-bold text-[#0A2540] mb-4">Areas We Serve</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">NSW Councils</h3>
              <div className="flex flex-wrap gap-2">
                {['City of Sydney', 'Canterbury-Bankstown', 'Waverley', 'Blacktown', 'The Hills', 'Central Coast', 'Parramatta', 'Northern Beaches', 'Inner West', 'Bayside', 'Randwick', 'Liverpool'].map((c, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{c}</span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">VIC Councils</h3>
              <div className="flex flex-wrap gap-2">
                {['City of Melbourne', 'Darebin', 'Merri-bek', 'Yarra', 'Port Phillip', 'Stonnington', 'Boroondara', 'Knox', 'Greater Geelong', 'Monash', 'Whitehorse', 'Glen Eira'].map((c, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
